import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useGetAWSAccountsQuery, isTokenGetterReady } from '../services/api';
import {
  resolveCompanyTenantIdFromAccount,
  resolveCompanyTenantIdFromRaw,
  resolveCompanyTenantIdFromAccountId,
  getCompanyDisplayName,
  formatWorkspaceDisplayName,
  getSingleCompanyTenantIdOverride,
  reportLoopbackTenantParityViolations,
  isForbiddenTenantOnCurrentHost,
} from '../utils/companyTenantResolver';

const AccountContext = createContext();

/** Sanitize tenant ids before they are placed into React state or sent in
 *  HTTP requests. Drops staging tenant ids on the production host so the
 *  app can never repeat the 2026-05-06 cross-environment leak. */
function sanitizeTenantIdForCurrentHost(tid) {
  const id = String(tid || '').trim();
  if (!id) return '';
  if (isForbiddenTenantOnCurrentHost(id)) {
    console.warn('[TENANT-GUARDRAIL] dropping forbidden tenant id on production host:', id);
    try {
      const cur = String(localStorage.getItem('selectedCompanyTenantId') || '').trim();
      if (cur === id) localStorage.removeItem('selectedCompanyTenantId');
      const act = String(localStorage.getItem('activeTenantId') || '').trim();
      if (act === id) localStorage.removeItem('activeTenantId');
    } catch (_) { /* ignore */ }
    return '';
  }
  return id;
}

/** Prefer saved workspace id; else resolve company tenant from legacy active/selected AWS ids. */
function readInitialResolvedCompanyTenantFromStorage() {
  try {
    const saved = String(localStorage.getItem('selectedCompanyTenantId') || '').trim();
    const overrideResult = getSingleCompanyTenantIdOverride();
    console.log('[TENANT-TRACE] readInitial:', { saved, overrideResult });
    if (saved && saved.startsWith('company_') && saved !== overrideResult) {
      console.log('[TENANT-TRACE] readInitial -> using provisioned:', saved);
      return sanitizeTenantIdForCurrentHost(saved);
    }
    if (overrideResult) {
      console.log('[TENANT-TRACE] readInitial -> using override:', overrideResult);
      return sanitizeTenantIdForCurrentHost(overrideResult);
    }
    const w = String(localStorage.getItem('selectedCompanyTenantId') || '').trim();
    if (w && w.toLowerCase() !== 'all') {
      // Map raw AWS account ids (e.g. legacy mock 982…) → canonical company tenant via REACT_APP_COMPANY_TENANT_MAP.
      return sanitizeTenantIdForCurrentHost(resolveCompanyTenantIdFromAccountId(w));
    }
    const a = String(localStorage.getItem('activeTenantId') || '').trim();
    const s = String(localStorage.getItem('selectedAccountId') || '').trim();
    const raw =
      (a && a.toLowerCase() !== 'all' ? a : '') || (s && s.toLowerCase() !== 'all' ? s : '');
    const resolved = raw ? resolveCompanyTenantIdFromRaw(raw, null) : '';
    return resolved && resolved.toLowerCase() !== 'all'
      ? sanitizeTenantIdForCurrentHost(resolved)
      : '';
  } catch {
    return '';
  }
}

export const useAccount = () => {
  const context = useContext(AccountContext);
  if (!context) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
};

export const AccountProvider = ({ children }) => {
  const { user } = useAuth0();

  const [tokenReady, setTokenReady] = useState(false);

  const [selectedCompanyTenantId, setSelectedCompanyTenantId] = useState(() => readInitialResolvedCompanyTenantFromStorage());

  const [activeTenantId, setActiveTenantId] = useState(() => {
    const r = readInitialResolvedCompanyTenantFromStorage();
    return r || null;
  });

  // Server-authoritative tenant list (from CompanyTenantSetupGate -> /signup/status).
  // When `serverTenantsLoaded` flips to true, companyWorkspaces is built from this
  // list rather than from REACT_APP_COMPANY_TENANT_MAP-resolved AWS accounts. This
  // is the production tenant guardrail: nothing baked into the bundle can override
  // the user's actual memberships.
  const [serverTenants, setServerTenants] = useState([]);
  const [serverTenantsLoaded, setServerTenantsLoaded] = useState(false);

  /** Atomic bootstrap from /signup/status. Sets selectedCompanyTenantId,
   *  activeTenantId, and the server-authoritative companyWorkspaces in one
   *  React update so children never render with mismatched tenant context. */
  const bootstrapServerTenantState = useCallback(({ tenants, defaultTenantId } = {}) => {
    const list = Array.isArray(tenants)
      ? tenants
        .map((t) => {
          const tid = sanitizeTenantIdForCurrentHost(t?.tenantId);
          if (!tid) return null;
          return {
            tenantId: tid,
            companyName: String(t?.companyName || '').trim(),
            primaryDomain: String(t?.primaryDomain || '').trim(),
            role: String(t?.role || '').trim() || 'member',
          };
        })
        .filter(Boolean)
      : [];
    const fromApi = sanitizeTenantIdForCurrentHost(defaultTenantId);
    const resolved =
      (fromApi && list.some((t) => t.tenantId === fromApi) ? fromApi : '')
      || (list.length ? list[0].tenantId : '');

    setServerTenants(list);
    setServerTenantsLoaded(true);

    if (resolved) {
      setSelectedCompanyTenantId(resolved);
      setActiveTenantId(resolved);
      try {
        localStorage.setItem('selectedCompanyTenantId', resolved);
        localStorage.setItem('activeTenantId', resolved);
      } catch (_) { /* ignore */ }
      console.log('[TENANT-TRACE] bootstrapServerTenantState:', resolved, 'tenantCount:', list.length);
    } else {
      setSelectedCompanyTenantId('');
      setActiveTenantId(null);
      try {
        localStorage.removeItem('selectedCompanyTenantId');
        localStorage.removeItem('activeTenantId');
      } catch (_) { /* ignore */ }
      console.warn('[TENANT-TRACE] bootstrapServerTenantState: no resolved tenant id (server returned empty)');
    }
  }, []);

  useEffect(() => {
    const checkTokenReady = () => {
      const ready = isTokenGetterReady();
      if (ready !== tokenReady) {
        setTokenReady(ready);
      }
    };
    checkTokenReady();
    const interval = setInterval(() => {
      checkTokenReady();
    }, 100);
    const timeout = setTimeout(() => {
      clearInterval(interval);
      setTokenReady(true);
    }, 5000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [tokenReady]);

  // Defensive: any path that smuggled a forbidden tenant id into state must
  // be reverted before tenantReady flips true — or the next render will fire
  // queries with a poison header. Production-host hard guard.
  useEffect(() => {
    const cur = String(selectedCompanyTenantId || '').trim();
    if (cur && isForbiddenTenantOnCurrentHost(cur)) {
      console.warn('[TENANT-GUARDRAIL] selectedCompanyTenantId became forbidden; clearing:', cur);
      setSelectedCompanyTenantId('');
      setActiveTenantId(null);
      try {
        localStorage.removeItem('selectedCompanyTenantId');
        localStorage.removeItem('activeTenantId');
      } catch (_) { /* ignore */ }
    }
  }, [selectedCompanyTenantId]);

  // True once a canonical tenant id is set. Tenant-guarded queries must skip until then —
  // the legacy behaviour of firing without a header (or with a stale localStorage tenant)
  // is what produced the 403/empty-Unibox outage on 2026-05-06.
  const tenantReady = Boolean(String(selectedCompanyTenantId || '').trim());

  const { data: awsAccountsData, isLoading: isLoadingAccounts, refetch: refetchAccounts, error: apiError } = useGetAWSAccountsQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    skip: !tokenReady || !tenantReady,
  });

  let apiAccounts = [];
  if (awsAccountsData) {
    if (awsAccountsData.accounts && Array.isArray(awsAccountsData.accounts)) {
      apiAccounts = awsAccountsData.accounts;
    } else if (Array.isArray(awsAccountsData)) {
      apiAccounts = awsAccountsData;
    } else if (awsAccountsData.data?.accounts && Array.isArray(awsAccountsData.data.accounts)) {
      apiAccounts = awsAccountsData.data.accounts;
    }
  }
  apiAccounts = Array.isArray(apiAccounts) ? apiAccounts.filter(Boolean) : [];

  const defaultMockAccounts = [
    {
      id: '982423662433',
      name: 'KrossGlobal-Marketing',
      alias: 'KrossGlobal',
      status: 'connected',
      region: 'us-east-1',
      domains: ['krossglobalmarketing.com', 'krossglobal.com', 'kross-marketing.com'],
      emailAccounts: 5,
      lastActivity: '2024-01-20T10:30:00Z',
    },
  ];

  const accounts = useMemo(() => {
    const apiHasLoaded = !isLoadingAccounts && (awsAccountsData !== undefined || apiError !== undefined);

    if (apiHasLoaded) {
      const apiAccountsMap = new Map();
      apiAccounts.forEach((account) => {
        apiAccountsMap.set(account.id, account);
      });

      let deletedMockAccounts = [];
      try {
        deletedMockAccounts = JSON.parse(localStorage.getItem('deletedMockAccounts') || '[]');
      } catch (error) {
        console.error('Error reading deleted mock accounts:', error);
      }

      defaultMockAccounts.forEach((mockAccount) => {
        // Production uses real /aws-accounts only; injecting the dev mock (982423662433) splits workspaces
        // and shows a raw 12-digit tenant id in the header dropdown.
        if (process.env.REACT_APP_ENV === 'production') return;
        if (!apiAccountsMap.has(mockAccount.id) && !deletedMockAccounts.includes(mockAccount.id)) {
          apiAccountsMap.set(mockAccount.id, mockAccount);
        }
      });

      return Array.from(apiAccountsMap.values());
    }

    if (isLoadingAccounts) {
      try {
        const savedAccounts = localStorage.getItem('awsAccounts');
        if (savedAccounts) {
          const parsed = JSON.parse(savedAccounts);
          if (Array.isArray(parsed) && parsed.length > 0) {
            return parsed;
          }
        }
      } catch (error) {
        console.error('Error loading accounts from localStorage:', error);
      }
      return process.env.REACT_APP_ENV === 'production' ? [] : defaultMockAccounts;
    }

    return process.env.REACT_APP_ENV === 'production' ? [] : defaultMockAccounts;
  }, [apiAccounts, awsAccountsData, isLoadingAccounts, apiError]);

  /** Tenant ids from Auth0 app_metadata (self-serve company provisioning). */
  const authMetadataTenantWorkspaces = useMemo(() => {
    const m = user?.app_metadata || {};
    const ids = [];
    if (Array.isArray(m.kg_tenant_ids)) {
      for (const x of m.kg_tenant_ids) {
        const t = String(x || '').trim();
        if (t && !ids.includes(t)) ids.push(t);
      }
    }
    const one = String(m.kg_tenant_id || '').trim();
    if (one && !ids.includes(one)) ids.push(one);
    return ids.map((tenantId) => ({
      companyTenantId: tenantId,
      awsAccounts: [],
      displayName: getCompanyDisplayName(tenantId) || formatWorkspaceDisplayName(tenantId, []),
    }));
  }, [user]);

  /** One row per company tenant; AWS accounts are grouped for infra (domains, etc.). */
  const companyWorkspaces = useMemo(() => {
    // Server-authoritative path. Once /signup/status has loaded, drive workspace
    // selection from the user's real memberships and ignore all baked tenant
    // mappings. This is the durable fix for the 2026-05-06 outage where the
    // production bundle shipped with REACT_APP_COMPANY_TENANT_MAP pointing at
    // staging tenant ids.
    if (serverTenantsLoaded && serverTenants.length) {
      const decorate = (ct, awsAccounts) => ({
        companyTenantId: ct,
        awsAccounts,
        displayName: formatWorkspaceDisplayName(ct, awsAccounts),
      });
      return serverTenants.map((t) => {
        const matchingAws = accounts.filter((a) => {
          if (!a) return false;
          const explicit = String(a.companyTenantId || '').trim();
          return explicit && explicit === t.tenantId;
        });
        const ws = decorate(t.tenantId, matchingAws);
        if (t.companyName && !ws.displayName) ws.displayName = t.companyName;
        if (t.companyName && (ws.displayName === t.tenantId)) ws.displayName = t.companyName;
        return ws;
      });
    }

    const single = getSingleCompanyTenantIdOverride();
    // getSingleCompanyTenantIdOverride() returns '' when a provisioned tenant is active
    // (to stop API calls from using the default). For workspace-list building we still
    // need the staging default as a base workspace, so read the env var directly.
    const envTenantId = String(process.env.REACT_APP_SINGLE_COMPANY_TENANT_ID || '').trim();
    const baseTenantId = single || envTenantId;
    const decorate = (ct, awsAccounts) => ({
      companyTenantId: ct,
      awsAccounts,
      displayName: formatWorkspaceDisplayName(ct, awsAccounts),
    });
    const mergeAuthOnlyTenants = (rows) => {
      const out = [...rows];
      for (const aw of authMetadataTenantWorkspaces) {
        if (!out.some((r) => r.companyTenantId === aw.companyTenantId)) {
          out.push(aw);
        }
      }
      return out;
    };
    if (baseTenantId) {
      const awsAccounts = accounts.filter((a) => a && a.id);
      const base = decorate(baseTenantId, awsAccounts);
      const extras = authMetadataTenantWorkspaces.filter((w) => w.companyTenantId !== baseTenantId);

      const lsTenant = String(localStorage.getItem('selectedCompanyTenantId') || '').trim();
      if (lsTenant && lsTenant.startsWith('company_') && lsTenant !== baseTenantId
          && !extras.some((w) => w.companyTenantId === lsTenant)) {
        extras.push({
          companyTenantId: lsTenant,
          awsAccounts: [],
          displayName: getCompanyDisplayName(lsTenant) || formatWorkspaceDisplayName(lsTenant, []),
        });
      }

      const result = extras.length === 0 ? [base] : [...extras, base];
      console.log('[TENANT-TRACE] companyWorkspaces:', { single, envTenantId, baseTenantId, lsTenant, result: result.map(w => w.companyTenantId) });
      return result;
    }
    const map = new Map();
    for (const acc of accounts) {
      if (!acc || !acc.id) continue;
      const ct = resolveCompanyTenantIdFromAccount(acc);
      if (!ct) continue;
      if (!map.has(ct)) {
        map.set(ct, { companyTenantId: ct, awsAccounts: [] });
      }
      map.get(ct).awsAccounts.push(acc);
    }
    const fromAws = Array.from(map.values()).map((w) => decorate(w.companyTenantId, w.awsAccounts));
    return mergeAuthOnlyTenants(fromAws);
  }, [accounts, authMetadataTenantWorkspaces, serverTenants, serverTenantsLoaded]);

  useEffect(() => {
    reportLoopbackTenantParityViolations(companyWorkspaces);
  }, [companyWorkspaces]);

  const applyWorkspace = useCallback(
    (companyTenantId) => {
      const ct = sanitizeTenantIdForCurrentHost(companyTenantId);
      if (!ct) return;
      const ws = companyWorkspaces.find((w) => w.companyTenantId === ct);
      console.log('[TENANT-TRACE] applyWorkspace called:', ct, ws ? 'FOUND in workspaces' : 'NOT IN workspaces');
      if (!ws) return;

      setSelectedCompanyTenantId(ct);
      setActiveTenantId(ct);
      try {
        localStorage.setItem('selectedCompanyTenantId', ct);
        localStorage.setItem('activeTenantId', ct);
      } catch (e) {
        console.error('Error persisting workspace tenant', e);
      }

      if (ws.awsAccounts.length === 1) {
        const id = ws.awsAccounts[0].id;
        setSelectedAccountId(id);
        try {
          localStorage.setItem('selectedAccountId', id);
        } catch (e) {
          console.error(e);
        }
      } else {
        setSelectedAccountId('all');
        try {
          localStorage.setItem('selectedAccountId', 'all');
        } catch (e) {
          console.error(e);
        }
      }
    },
    [companyWorkspaces]
  );

  const [selectedAccountId, setSelectedAccountId] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingTimeoutId, setLoadingTimeoutId] = useState(null);

  const selectedAccount =
    selectedAccountId === 'all'
      ? { id: 'all', name: 'All linked hosting accounts', alias: 'All linked', status: 'connected' }
      : accounts.find((account) => account.id === selectedAccountId) || {
          id: 'all',
          name: 'All linked hosting accounts',
          alias: 'All linked',
          status: 'connected',
        };

  const workspaceAwsAccounts = useMemo(() => {
    if (!selectedCompanyTenantId) return accounts;
    const filtered = accounts.filter((a) => resolveCompanyTenantIdFromAccount(a) === selectedCompanyTenantId);
    return filtered.length ? filtered : accounts;
  }, [accounts, selectedCompanyTenantId]);

  const workspaceLabel = useMemo(() => {
    const w = companyWorkspaces.find((x) => x.companyTenantId === selectedCompanyTenantId);
    if (w) return w.displayName;
    const mapped = resolveCompanyTenantIdFromAccountId(selectedCompanyTenantId);
    const w2 = companyWorkspaces.find((x) => x.companyTenantId === mapped);
    if (w2) return w2.displayName;
    if (selectedCompanyTenantId) {
      return formatWorkspaceDisplayName(selectedCompanyTenantId, workspaceAwsAccounts) || 'Workspace';
    }
    return 'Workspace';
  }, [companyWorkspaces, selectedCompanyTenantId, workspaceAwsAccounts]);

  const selectWorkspace = applyWorkspace;

  const selectAccount = (accountId) => {
    if (loadingTimeoutId) {
      clearTimeout(loadingTimeoutId);
    }
    setIsLoading(true);

    if (accountId === 'all') {
      const first = companyWorkspaces[0];
      if (first) {
        applyWorkspace(first.companyTenantId);
      } else {
        setSelectedAccountId('all');
        try {
          localStorage.setItem('selectedAccountId', 'all');
        } catch (e) {
          console.error(e);
        }
      }
    } else {
      const acc = accounts.find((a) => a.id === accountId);
      const ct = sanitizeTenantIdForCurrentHost(
        resolveCompanyTenantIdFromAccount(acc || { id: accountId })
      );
      setSelectedCompanyTenantId(ct);
      setActiveTenantId(ct || null);
      try {
        if (ct) {
          localStorage.setItem('selectedCompanyTenantId', ct);
          localStorage.setItem('activeTenantId', ct);
        }
        localStorage.setItem('selectedAccountId', accountId);
      } catch (e) {
        console.error(e);
      }
      setSelectedAccountId(accountId);
    }

    const timeoutId = setTimeout(() => {
      setIsLoading(false);
      setLoadingTimeoutId(null);
    }, 500);
    setLoadingTimeoutId(timeoutId);
  };

  useEffect(() => {
    return () => {
      if (loadingTimeoutId) {
        clearTimeout(loadingTimeoutId);
      }
    };
  }, [loadingTimeoutId]);

  useEffect(() => {
    if (!companyWorkspaces.length) return;
    const savedCt = String(localStorage.getItem('selectedCompanyTenantId') || '').trim();
    const directMatch = savedCt && companyWorkspaces.some((w) => w.companyTenantId === savedCt);
    const workspaceIds = companyWorkspaces.map((w) => w.companyTenantId);
    console.log('[TENANT-TRACE] autoSelect useEffect:', { savedCt, directMatch, workspaceIds });
    if (directMatch) {
      console.log('[TENANT-TRACE] autoSelect -> directMatch:', savedCt);
      applyWorkspace(savedCt);
    } else {
      const normalized = savedCt ? resolveCompanyTenantIdFromAccountId(savedCt) : '';
      const normMatch = normalized && companyWorkspaces.some((w) => w.companyTenantId === normalized);
      if (normMatch) {
        console.log('[TENANT-TRACE] autoSelect -> normalizedMatch:', normalized);
        applyWorkspace(normalized);
      } else {
        console.log('[TENANT-TRACE] autoSelect -> fallback[0]:', companyWorkspaces[0].companyTenantId);
        applyWorkspace(companyWorkspaces[0].companyTenantId);
      }
    }
    setTimeout(() => setIsLoading(false), 100);
  }, [companyWorkspaces, applyWorkspace]);

  /** If React state still has a stale pseudo-tenant (e.g. raw 12-digit) after workspaces hydrate, snap to a valid row. */
  useEffect(() => {
    if (!companyWorkspaces.length) return;
    const valid = new Set(companyWorkspaces.map((x) => x.companyTenantId));
    if (valid.has(selectedCompanyTenantId)) return;
    const mapped = resolveCompanyTenantIdFromAccountId(selectedCompanyTenantId);
    if (mapped && valid.has(mapped)) {
      applyWorkspace(mapped);
      return;
    }
    applyWorkspace(companyWorkspaces[0].companyTenantId);
  }, [companyWorkspaces, selectedCompanyTenantId, applyWorkspace]);

  const deleteAccount = (accountId) => {
    const mockAccountIds = ['123456789012', '987654321098'];
    if (mockAccountIds.includes(accountId)) {
      try {
        const deletedMockAccounts = JSON.parse(localStorage.getItem('deletedMockAccounts') || '[]');
        if (!deletedMockAccounts.includes(accountId)) {
          deletedMockAccounts.push(accountId);
          localStorage.setItem('deletedMockAccounts', JSON.stringify(deletedMockAccounts));
        }
      } catch (error) {
        console.error('Error saving deleted mock accounts:', error);
      }
    }

    if (selectedAccountId === accountId) {
      setSelectedAccountId('all');
      localStorage.setItem('selectedAccountId', 'all');
    }

    refetchAccounts();
  };

  useEffect(() => {
    if (apiAccounts.length === 0 && accounts.length > 0) {
      try {
        localStorage.setItem('awsAccounts', JSON.stringify(accounts));
      } catch (error) {
        console.error('Error saving accounts to localStorage:', error);
      }
    }
  }, [accounts, apiAccounts.length]);

  const value = {
    accounts,
    workspaceAwsAccounts,
    companyWorkspaces,
    selectedCompanyTenantId,
    workspaceLabel,
    selectWorkspace,
    selectedAccount,
    selectedAccountId,
    activeTenantId,
    tenantReady,
    selectAccount,
    deleteAccount,
    refetchAccounts,
    bootstrapServerTenantState,
    serverTenantsLoaded,
    isLoading: isLoading || isLoadingAccounts,
    isAllAccountsSelected: selectedAccountId === 'all',
  };

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
};
